import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import AdvancedAutomations from './pages/AdvancedAutomations';
import Templates from './pages/Templates';
import Logs from './pages/Logs';
import Settings from './pages/Settings';
import Senders from './pages/Senders';
import Monitoring from './pages/Monitoring';
import Callbacks from './pages/Callbacks';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Extract token from URL SYNCHRONOUSLY before React renders ────────────────
// Prevents race condition where ProtectedRoute checks localStorage too early
;(function extractAuthToken() {
  try {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      localStorage.setItem('wid-token', token);
      window.history.replaceState({}, '', window.location.pathname);
    }
  } catch (e) { /* ignore */ }
})();

// ─── Axios global interceptor ─────────────────────────────────────────────────
axios.interceptors.request.use((config) => {
  // Bearer token handles auth — withCredentials causes CORS issues with wildcard origin
  config.withCredentials = false;
  const token = localStorage.getItem('wid-token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});
export const ThemeContext = createContext({ theme: 'dark', toggleTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

// ─── Protected route ──────────────────────────────────────────────────────────
function ProtectedRoute() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('wid-token');
    if (!token) { setIsAuthenticated(false); return; }

    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(() => setIsAuthenticated(true))
      .catch(() => {
        localStorage.removeItem('wid-token');
        setIsAuthenticated(false);
      });
  }, [location.pathname]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <DashboardLayout />;
}

// ─── App Router ───────────────────────────────────────────────────────────────
function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<ProtectedRoute />}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:clientId" element={<ClientDetail />} />
        <Route path="automations" element={<AdvancedAutomations />} />
        <Route path="templates" element={<Templates />} />
        <Route path="senders" element={<Senders />} />
        <Route path="monitoring" element={<Monitoring />} />
        <Route path="callbacks" element={<Callbacks />} />
        <Route path="logs" element={<Logs />} />
        <Route path="settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('wid-theme') || 'dark');

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('wid-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors theme={theme} />
    </ThemeContext.Provider>
  );
}

export default App;
